import { useContext, useEffect, useRef, useState } from "react";
import { Modal, Table } from "react-bootstrap";
import styled from "styled-components";

import FunctionButton from "common/components/atoms/FunctionButton";

import { NameID } from "common/types/NameID";
import AuthContext from "common/store/AuthContext";

import { ChartInfo } from "../types/ChartInfo";
import { ChartTypes } from "../types/ChartTypes";
import { useChartData } from "../hooks/useChartData";
import { SelectGraphRow } from "./SelectGraphRow";
import DraggableModal from "common/components/molecules/DraggableModal";
import { ModalButtons } from "common/components/molecules/ModalButtons";

type Props = {
  candidates: NameID[];
  charts: ChartInfo[];
  onOK: (newLists: ChartInfo[]) => void;
  onCancel: () => void;
};

export const SelectGraphsDlg = (props: Props) => {
  const [charts, setCharts] = useState<ChartInfo[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const { getChartTitle } = useChartData();
  const { selectedPark } = useContext(AuthContext);

  const bodyRef = useRef<HTMLDivElement>();

  const addGraph = () => {
    setCharts([...charts, { chartId: -1, numCols: 1, numRows: 1 }]);
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  };

  useEffect(() => {
    if (props.charts.length === 0) addGraph();
    else
      setCharts(
        props.charts.map((x) => {
          return { ...x };
        })
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.charts]);

  const handleDragStart = (e: React.DragEvent<HTMLTableRowElement>, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent<HTMLTableRowElement>, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent<HTMLTableRowElement>, dropIndex: number) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newCharts = [...charts];
    const [draggedItem] = newCharts.splice(draggedIndex, 1);
    newCharts.splice(dropIndex, 0, draggedItem);

    setCharts(newCharts);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  return (
    <Modal show={true} size="lg" dialogAs={DraggableModal}>
      <Modal.Header className="handle">
        <Modal.Title>グラフ・表の選択</Modal.Title>
      </Modal.Header>
      <SModalBody ref={bodyRef}>
        <Table>
          <thead>
            <tr className="text-center">
              <th className="col-md-auto" style={{ minWidth: "30px" }}>
                {/* ドラッグハンドル用 */}
              </th>
              <th className="col-md-auto" style={{ minWidth: "200px" }}>
                グラフ・表の種類
              </th>
              <th className="col-md-2" style={{ minWidth: "80px" }}>
                横{" "}
              </th>
              <th className="col-md-2" style={{ minWidth: "80px" }}>
                縦
              </th>
              <th className="col-md-1">操作</th>
            </tr>
          </thead>
          <tbody>
            {charts.map((chart, index) => (
              <SDraggableRow
                key={`chart_${chart.chartId}_${index}`}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
                $isDragging={draggedIndex === index}
                $isDragOver={dragOverIndex === index}
              >
                {/* <SDragHandle>
                  <i className="fas fa-grip-vertical"></i>
                </SDragHandle> */}
                <SelectGraphRow
                  chart={{
                    chartId: chart.chartId,
                    numCols: chart.numCols,
                    numRows: chart.numRows,
                  }}
                  // 選択一覧 (タイトルを公園の設定に合せる)
                  candidates={ChartTypes.map<NameID>((x) => ({
                    id: x.id,
                    name: getChartTitle(x.id, selectedPark?.dailyTabs) ?? x.name,
                  }))}
                  updated={(newChart) => {
                    charts[index] = newChart;
                    setCharts([...charts]);
                  }}
                  deleteRequested={() => {
                    setCharts(charts.filter((_, i) => i !== index));
                  }}
                />
              </SDraggableRow>
            ))}
          </tbody>
        </Table>
      </SModalBody>
      <Modal.Footer>
        <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
          <FunctionButton
            onClick={(e) => {
              e.preventDefault();
              addGraph();
            }}
            className="ms-2"
            tooltip="追加"
          >
            <i className="fa-regular fa-plus"></i>
          </FunctionButton>
          <div>
            <ModalButtons
              okDisabled={charts.find((x) => x.chartId < 0) !== undefined}
              onOk={() => {
                props.onOK(charts);
              }}
              okText="決定"
              onCancel={props.onCancel}
            />
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

const SModalBody = styled(Modal.Body)`
  max-height: 400px;
  overflow: auto;
`;

const SDraggableRow = styled.tr<{ $isDragging: boolean; $isDragOver: boolean }>`
  cursor: move;
  opacity: ${(props) => (props.$isDragging ? 0.5 : 1)};
  background-color: ${(props) => (props.$isDragOver ? "#e3f2fd" : "transparent")};

  &:hover {
    background-color: #f5f5f5;
  }

  transition: background-color 0.2s ease, opacity 0.2s ease;
`;
